/**
 * Aceternity UI — Stateful Button
 * https://ui.aceternity.com/components/stateful-button
 */
import React from 'react';
import { motion, useAnimate } from 'framer-motion';
import { cn } from '../../lib/utils';

interface StatefulButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  className?: string;
  children: React.ReactNode;
}

export const StatefulButton = ({ className, children, ...props }: StatefulButtonProps) => {
  const [scope, animate] = useAnimate();

  const animateLoading = async () => {
    await animate(
      '.loader',
      {
        width: '20px',
        scale: 1,
        display: 'block',
      },
      { duration: 0.2 },
    );
  };

  const animateSuccess = async () => {
    await animate(
      '.loader',
      {
        width: '0px',
        scale: 0,
        display: 'none',
      },
      { duration: 0.2 },
    );
    await animate(
      '.check',
      {
        width: '20px',
        scale: 1,
        display: 'block',
      },
      { duration: 0.2 },
    );

    await animate(
      '.check',
      {
        width: '0px',
        scale: 0,
        display: 'none',
      },
      {
        delay: 1.2,
        duration: 0.2,
      },
    );
  };

  const handleClick = async (event: React.MouseEvent<HTMLButtonElement>) => {
    await animateLoading();
    await props.onClick?.(event);
    await animateSuccess();
  };

  const { onClick, ...buttonProps } = props;

  return (
    <button
      ref={scope as React.Ref<HTMLButtonElement>}
      type={buttonProps.type ?? 'button'}
      className={cn(
        'flex min-w-[120px] cursor-pointer items-center justify-center gap-2 rounded-full bg-green-500 px-4 py-2 font-semibold text-white ring-offset-2 transition duration-200 hover:ring-2 hover:ring-green-500 dark:ring-offset-black',
        className,
      )}
      {...buttonProps}
      onClick={handleClick}
    >
      <motion.div layout className="flex items-center gap-2">
        <Loader />
        <CheckIcon />
        <motion.span layout>{children}</motion.span>
      </motion.div>
    </button>
  );
};

const Loader = () => {
  return (
    <motion.svg
      animate={{ rotate: [0, 360] }}
      initial={{ scale: 0, width: 0, display: 'none' }}
      style={{ scale: 0.5, display: 'none' }}
      transition={{ duration: 0.3, repeat: Infinity, ease: 'linear' }}
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="loader text-white"
    >
      <path stroke="none" d="M0 0h24v24H0z" fill="none" />
      <path d="M12 3a9 9 0 1 0 9 9" />
    </motion.svg>
  );
};

const CheckIcon = () => {
  return (
    <motion.svg
      initial={{ scale: 0, width: 0, display: 'none' }}
      style={{ scale: 0.5, display: 'none' }}
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="check text-white"
    >
      <path stroke="none" d="M0 0h24v24H0z" fill="none" />
      <path d="M12 12m-9 0a9 9 0 1 0 18 0a9 9 0 1 0 -18 0" />
      <path d="M9 12l2 2l4 -4" />
    </motion.svg>
  );
};
